import React, { useState, useEffect, useCallback } from 'react'
import styled from '@emotion/styled'
import { rgba, darken, lighten } from 'polished'
import { keyframes } from '@emotion/react'
// import { NavLink, withRouter } from 'react-router-dom'
import { NavLink, useLocation } from 'react-router-dom'
import PerfectScrollbar from 'react-perfect-scrollbar'
import '../vendor/perfect-scrollbar.css'

// import { spacing } from '@mui/system'

import {
  Badge,
  Box as MuiBox,
  Chip,
  Grid,
  Avatar,
  Collapse,
  Drawer as MuiDrawer,
  // List as MuiList,
  ListItemIcon,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography
} from '@mui/material'

import { ExpandLess, ExpandMore } from '@mui/icons-material'

import { green } from '@mui/material/colors'

import {
  sidebarRoutes_Owner,
  sidebarRoutes_HAdmin,
  sidebarRoutes_Admin,
  sidebarRoutes_FOwner
} from '../routes/index'

import { ReactComponent as Logo } from '../vendor/logo.svg'

// Helper function to normalize and compare paths
const isPathMatch = (currentPath, targetPath) => {
  if (!targetPath) return false

  // Normalize paths (remove trailing slashes, convert to lowercase)
  const normalize = (path) => path?.toLowerCase().replace(/\/$/, '') || ''
  const current = normalize(currentPath)
  const target = normalize(targetPath)

  // Exact match
  if (current === target) return true

  // Handle root path
  if (target === '' && current === '') return true

  return false
}

// Professional Keyframe animations - Subtle and refined
const subtleSparkle = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(0.8);
  }
  50% {
    opacity: 0.6;
    transform: scale(1);
  }
`

const smoothShimmer = keyframes`
  0% {
    background-position: -100% center;
    opacity: 0;
  }
  50% {
    opacity: 0.15;
  }
  100% {
    background-position: 200% center;
    opacity: 0;
  }
`

const fadeInSmooth = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const pulseGlow = keyframes`
  0%, 100% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
`

const Box = styled(MuiBox)(({ theme }) => ({
  margin: theme.spacing(1)
}))

const Drawer = styled(MuiDrawer)`
  border-right: 0;

  > div {
    border-right: 0;
    box-shadow: 2px 0 16px rgba(0, 0, 0, 0.08), 4px 0 32px rgba(0, 0, 0, 0.04),
      0 0 1px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(20px) saturate(180%);
    background: ${(props) => props.theme.sidebar.background};
    position: fixed;
    height: 100vh;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 1px;
      background: linear-gradient(
        180deg,
        transparent 0%,
        ${(props) => rgba(props.theme.palette.primary.main, 0.1)} 50%,
        transparent 100%
      );
    }
  }
`

const Scrollbar = styled(PerfectScrollbar)`
  background: ${(props) => props.theme.sidebar.background};
  border-right: 1px solid ${(props) => rgba(props.theme.sidebar.color, 0.06)};

  .ps__rail-y {
    width: 6px;
    background-color: transparent;
    opacity: 0;
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      opacity: 1;
    }

    .ps__thumb-y {
      width: 4px;
      right: 1px;
      background: ${(props) => rgba(props.theme.palette.primary.main, 0.4)};
      border-radius: 3px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover {
        width: 4px;
        background: ${(props) => rgba(props.theme.palette.primary.main, 0.6)};
        box-shadow: 0 0 4px
          ${(props) => rgba(props.theme.palette.primary.main, 0.3)};
      }
    }
  }

  &:hover .ps__rail-y {
    opacity: 1;
  }
`

// const List = styled(MuiList)`
//   background-color: ${(props) => props.theme.sidebar.background};
// `

const Items = styled.div`
  padding-top: ${(props) => props.theme.spacing(2)}px;
  padding-bottom: ${(props) => props.theme.spacing(2.5)}px;

  /* Ensure proper NavLink active styling */
  .active {
    /* This class is added by NavLink when route is active */
  }
`

const Brand = styled(ListItem)`
  font-size: ${(props) => props.theme.typography.h5.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => props.theme.sidebar.header.color};
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.sidebar.header.background} 0%,
    ${(props) => darken(0.03, props.theme.sidebar.header.background)} 50%,
    ${(props) => darken(0.02, props.theme.sidebar.header.background)} 100%
  );
  font-family: ${(props) => props.theme.typography.fontFamily};
  min-height: 72px;
  padding: ${(props) => props.theme.spacing(3)}px
    ${(props) => props.theme.spacing(6)}px;
  justify-content: center;
  cursor: pointer;
  border-bottom: 1px solid ${(props) => rgba(props.theme.sidebar.color, 0.1)};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      ${(props) => rgba(props.theme.palette.primary.main, 0.1)},
      transparent
    );
    transition: left 0.6s ease;
  }

  ${(props) => props.theme.breakpoints.up('sm')} {
    min-height: 80px;
  }

  img {
    filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.15));
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    z-index: 1;
  }

  &:hover {
    background: linear-gradient(
      135deg,
      ${(props) => lighten(0.015, props.theme.sidebar.header.background)} 0%,
      ${(props) => props.theme.sidebar.header.background} 50%,
      ${(props) => darken(0.01, props.theme.sidebar.header.background)} 100%
    );
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.06);

    &::before {
      left: 100%;
    }

    img {
      transform: scale(1.03);
      filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.18));
    }
  }
`

const BrandIcon = styled(Logo)`
  margin-right: ${(props) => props.theme.spacing(2)}px;
  color: ${(props) => props.theme.sidebar.header.brand.color};
  fill: ${(props) => props.theme.sidebar.header.brand.color};
  width: 32px;
  height: 32px;
`

const BrandChip = styled(Chip)`
  background-color: ${green[700]};
  border-radius: 5px;
  color: ${(props) => props.theme.palette.common.white};
  font-size: 55%;
  height: 18px;
  margin-left: 2px;
  margin-top: -16px;
  padding: 3px 0;

  span {
    padding-left: ${(props) => props.theme.spacing(1.375)}px;
    padding-right: ${(props) => props.theme.spacing(1.375)}px;
  }
`

const Category1 = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== '$hasActiveChild'
})(({ theme, $hasActiveChild }) => ({
  margin: theme.spacing(1, 2),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  borderRadius: '14px',
  fontWeight: 500,
  fontSize: '14px',
  color: theme.sidebar.color,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  background: $hasActiveChild
    ? `linear-gradient(135deg, ${rgba(
        theme.palette.primary.main,
        0.05
      )} 0%, ${rgba(theme.palette.primary.main, 0.08)} 100%)`
    : 'transparent',

  // Accent bar on left
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '4px',
    height: $hasActiveChild ? '50%' : 0,
    background: `linear-gradient(180deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
    borderRadius: '0 4px 4px 0',
    transition: 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: `0 0 10px ${rgba(theme.palette.primary.main, 0.5)}`
  },

  // Shimmer effect overlay
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, transparent, ${rgba(
      theme.palette.primary.main,
      0.1
    )}, transparent)`,
    transition: 'left 0.5s ease',
    pointerEvents: 'none'
  },

  svg: {
    color: $hasActiveChild ? theme.palette.primary.main : theme.sidebar.color,
    fontSize: '24px',
    width: '24px',
    height: '24px',
    opacity: $hasActiveChild ? 0.9 : 0.7,
    marginRight: theme.spacing(2),
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    filter: $hasActiveChild
      ? `drop-shadow(0 0 4px ${rgba(theme.palette.primary.main, 0.3)})`
      : 'drop-shadow(0 0 0 transparent)',
    zIndex: 1
  },

  '&:hover': {
    background: `linear-gradient(135deg, ${rgba(
      theme.palette.primary.main,
      0.08
    )} 0%, ${rgba(theme.palette.primary.main, 0.12)} 100%)`,
    boxShadow: `
      0 2px 8px ${rgba(theme.palette.primary.main, 0.15)},
      inset 0 1px 0 ${rgba(255, 255, 255, 0.08)}
    `,
    transform: 'translateX(4px)',

    '&::after': {
      left: '100%'
    },

    '&::before': {
      height: '60%',
      boxShadow: `0 0 6px ${rgba(theme.palette.primary.main, 0.3)}`
    },

    svg: {
      opacity: 1,
      color: theme.palette.primary.main,
      filter: `drop-shadow(0 2px 4px ${rgba(theme.palette.primary.main, 0.3)})`,
      transform: 'scale(1.08)'
    }
  },

  '&.Mui-selected': {
    background: `linear-gradient(135deg, ${rgba(
      theme.palette.primary.main,
      0.15
    )} 0%, ${rgba(theme.palette.primary.main, 0.2)} 100%)`,
    fontWeight: 600,
    boxShadow: `
      0 2px 8px ${rgba(theme.palette.primary.main, 0.25)},
      0 4px 16px ${rgba(theme.palette.primary.main, 0.15)},
      inset 0 1px 0 ${rgba(255, 255, 255, 0.1)}
    `,
    border: `1px solid ${rgba(theme.palette.primary.main, 0.3)}`,

    '&::before': {
      height: '70%',
      width: '4px',
      boxShadow: `0 0 8px ${rgba(theme.palette.primary.main, 0.5)}`,
      animation: `${pulseGlow} 3s ease-in-out infinite`
    },

    '&::after': {
      animation: `${smoothShimmer} 4s ease-in-out infinite`,
      background: `linear-gradient(90deg, transparent, ${rgba(
        theme.palette.primary.main,
        0.15
      )}, transparent)`
    },

    svg: {
      color: theme.palette.primary.main,
      opacity: 1,
      filter: `drop-shadow(0 2px 4px ${rgba(theme.palette.primary.main, 0.4)})`,
      transform: 'scale(1.1)'
    },

    span: {
      color: theme.sidebar.color,
      fontWeight: 600,
      letterSpacing: '0.3px'
    }
  }
}))

const Category = styled(ListItem)`
  margin: 10;
  padding-top: ${(props) => props.theme.spacing(3)}px;
  padding-bottom: ${(props) => props.theme.spacing(3)}px;
  padding-left: ${(props) => props.theme.spacing(8)}px;
  padding-right: ${(props) => props.theme.spacing(8)}px;
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};

  svg {
    color: ${(props) => props.theme.sidebar.color};
    font-size: 20px;
    width: 20px;
    height: 20px;
    opacity: 0.5;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }

  &.${(props) => props.activeClassName} {
    background-color: ${(props) =>
      darken(0.03, props.theme.sidebar.background)};

    span {
      color: ${(props) => props.theme.sidebar.color};
    }
  }
`

const CategoryText = styled(ListItemText)`
  margin: 0;
  span {
    color: ${(props) => props.theme.sidebar.color};
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    padding: 0 ${(props) => props.theme.spacing(8)}px;
  }
`

const CategoryIconLess = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.7)};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(0deg);
  font-size: 24px;

  &:hover {
    color: ${(props) => props.theme.palette.primary.main};
    transform: rotate(-90deg) scale(1.1);
  }
`

const CategoryIconMore = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.7)};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(0deg);
  font-size: 24px;

  &:hover {
    color: ${(props) => props.theme.palette.primary.main};
    transform: rotate(90deg) scale(1.1);
  }
`

const SubMenuWrapper = styled.div`
  position: relative;
  margin-left: ${(props) => props.theme.spacing(4)}px;
  padding-left: ${(props) => props.theme.spacing(2)}px;
  animation: ${fadeInSmooth} 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    left: ${(props) => props.theme.spacing(2)}px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(
      180deg,
      transparent 0%,
      ${(props) => rgba(props.theme.palette.primary.main, 0.25)} 10%,
      ${(props) => rgba(props.theme.palette.primary.main, 0.25)} 90%,
      transparent 100%
    );
    border-radius: 2px;
    box-shadow: 0 0 4px
      ${(props) => rgba(props.theme.palette.primary.main, 0.2)};
  }
`

const Link = styled(ListItem)`
  padding-left: ${(props) => props.theme.spacing(17.5)}px;
  padding-top: ${(props) => props.theme.spacing(2)}px;
  padding-bottom: ${(props) => props.theme.spacing(2)}px;

  span {
    color: ${(props) => rgba(props.theme.sidebar.color, 0.7)};
  }

  &:hover span {
    color: ${(props) => rgba(props.theme.sidebar.color, 0.9)};
  }

  &:hover {
    background-color: ${(props) =>
      darken(0.015, props.theme.sidebar.background)};
  }

  &.${(props) => props.activeClassName} {
    background-color: ${(props) =>
      darken(0.03, props.theme.sidebar.background)};

    span {
      color: ${(props) => props.theme.sidebar.color};
    }
  }
`

const SubMenuLink = styled(ListItemButton)(({ theme }) => ({
  margin: theme.spacing(0.5, 2, 0.5, 4),
  marginLeft: theme.spacing(6),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(2),
  borderRadius: '10px',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: '13px',
  color: theme.sidebar.color,
  position: 'relative',
  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
  background: rgba(theme.sidebar.background, 0.3),
  backdropFilter: 'blur(10px)',

  // Connector line to parent
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '-16px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '12px',
    height: '2px',
    background: `linear-gradient(90deg, transparent, ${rgba(
      theme.palette.primary.main,
      0.3
    )})`,
    transition: 'all 0.35s ease'
  },

  // Sparkle dots on selection
  '&::after': {
    content: '""',
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    background: theme.palette.primary.main,
    opacity: 0,
    transition: 'all 0.35s ease',
    boxShadow: `0 0 8px ${rgba(theme.palette.primary.main, 0.6)}`
  },

  svg: {
    color: theme.sidebar.color,
    fontSize: '18px',
    width: '18px',
    height: '18px',
    opacity: 0.6,
    marginRight: theme.spacing(1.5),
    transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 1
  },

  span: {
    color: rgba(theme.sidebar.color, 0.75),
    fontSize: '13px',
    transition: 'all 0.35s ease'
  },

  '&:hover': {
    background: `linear-gradient(135deg, ${rgba(
      theme.palette.primary.main,
      0.06
    )} 0%, ${rgba(theme.palette.primary.main, 0.09)} 100%)`,
    paddingLeft: theme.spacing(3.5),
    transform: 'translateX(3px)',
    boxShadow: `
      0 1px 4px ${rgba(theme.palette.primary.main, 0.12)},
      inset 0 1px 0 ${rgba(255, 255, 255, 0.06)}
    `,

    '&::before': {
      width: '14px',
      background: `linear-gradient(90deg, transparent, ${rgba(
        theme.palette.primary.main,
        0.4
      )})`,
      boxShadow: `0 0 3px ${rgba(theme.palette.primary.main, 0.2)}`
    },

    svg: {
      opacity: 0.95,
      color: theme.palette.primary.main,
      transform: 'scale(1.1) translateX(1px)',
      filter: `drop-shadow(0 1px 2px ${rgba(theme.palette.primary.main, 0.3)})`
    },

    span: {
      color: rgba(theme.sidebar.color, 1),
      fontWeight: 500
    }
  },

  '&.Mui-selected, &.active': {
    background: `linear-gradient(135deg, ${rgba(
      theme.palette.primary.main,
      0.12
    )} 0%, ${rgba(theme.palette.primary.main, 0.18)} 100%)`,
    fontWeight: 600,
    boxShadow: `
      0 1px 4px ${rgba(theme.palette.primary.main, 0.2)},
      0 2px 8px ${rgba(theme.palette.primary.main, 0.15)},
      inset 0 1px 0 ${rgba(255, 255, 255, 0.1)}
    `,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(2.7),

    '&::before': {
      width: '16px',
      height: '2px',
      background: `linear-gradient(90deg, ${rgba(
        theme.palette.primary.main,
        0.6
      )}, ${theme.palette.primary.main})`,
      boxShadow: `0 0 4px ${rgba(theme.palette.primary.main, 0.4)}`
    },

    '&::after': {
      opacity: 0.8,
      animation: `${subtleSparkle} 3s ease-in-out infinite`
    },

    svg: {
      opacity: 1,
      color: theme.palette.primary.main,
      transform: 'scale(1.15)',
      filter: `drop-shadow(0 1px 3px ${rgba(theme.palette.primary.main, 0.4)})`
    },

    span: {
      color: theme.palette.primary.main,
      fontWeight: 600,
      letterSpacing: '0.2px'
    }
  }
}))

const LinkText = styled(ListItemText)`
color: ${(props) => props.theme.sidebar.color};
  span {
  font - size: ${(props) => props.theme.typography.body1.fontSize} px;
}
margin - top: 0;
margin - bottom: 0;
`

const LinkBadge = styled(Chip)`
font - size: 11px;
font - weight: ${(props) => props.theme.typography.fontWeightBold};
height: 20px;
position: absolute;
right: 28px;
top: 8px;
background: ${(props) => props.theme.sidebar.badge.background};

span.MuiChip - label,
  span.MuiChip - label:hover {
  cursor: pointer;
  color: ${(props) => props.theme.sidebar.badge.color};
  padding - left: ${(props) => props.theme.spacing(2)} px;
  padding - right: ${(props) => props.theme.spacing(2)} px;
}
`

const CategoryBadge = styled(LinkBadge)`
  top: 12px;
`

const SidebarSection = styled(Typography)`
  color: ${(props) => props.theme.sidebar.color};
  padding: ${(props) => props.theme.spacing(4)}px
    ${(props) => props.theme.spacing(4)}px
    ${(props) => props.theme.spacing(2)}px;
  opacity: 0.6;
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  position: relative;
  margin-top: ${(props) => props.theme.spacing(2)}px;

  &::after {
    content: '';
    position: absolute;
    bottom: 8px;
    left: ${(props) => props.theme.spacing(4)}px;
    right: ${(props) => props.theme.spacing(4)}px;
    height: 1px;
    background: linear-gradient(
      90deg,
      ${(props) => rgba(props.theme.sidebar.color, 0.2)},
      transparent
    );
  }
`

const SidebarFooter = styled.div`
background - color: ${(props) =>
  props.theme.sidebar.footer.background} !important;
padding: ${(props) => props.theme.spacing(2.75)}px ${(props) =>
  props.theme.spacing(4)} px;
border - right: 1px solid rgba(0, 0, 0, 0.12);
`

const SidebarFooterText = styled(Typography)`
  color: ${(props) => props.theme.sidebar.footer.color};
`

const SidebarFooterSubText = styled(Typography)`
color: ${(props) => props.theme.sidebar.footer.color};
font - size: 0.7rem;
display: block;
padding: 1px;
`

const SidebarFooterBadge = styled(Badge)`
margin - right: ${(props) => props.theme.spacing(1)} px;
  span {
  background - color: ${(props) =>
    props.theme.sidebar.footer.online.background};
  border: 1.5px solid ${(props) => props.theme.palette.common.white};
  height: 12px;
  width: 12px;
  border - radius: 50 %;
}
`

let routes = []

function SidebarCategory({
  name,
  icon,
  classes,
  isOpen,
  isCollapsable,
  badge,
  to,
  children,
  selected: selectedProp,
  className: classNameProp,
  ...rest
}) {
  const location = useLocation()

  // For direct links wrapped in NavLink, use the prop passed from NavLink
  // For collapsable categories, calculate based on children
  const isActive =
    selectedProp !== undefined
      ? selectedProp
      : !isCollapsable && to && isPathMatch(location.pathname, to)

  // Check if any child is active (for collapsable categories)
  const hasActiveChild =
    isCollapsable &&
    children &&
    children.some((child) => isPathMatch(location.pathname, child.path))

  return (
    <Category1
      disableGutters
      selected={isActive}
      $hasActiveChild={hasActiveChild}
      className={classNameProp || (isActive ? 'active' : '')}
      {...rest}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText>{name}</ListItemText>
      {isCollapsable ? (
        isOpen ? (
          <CategoryIconMore />
        ) : (
          <CategoryIconLess />
        )
      ) : null}
      {badge ? <CategoryBadge label={badge} /> : ''}
    </Category1>
  )
}

function SidebarLink({ name, to, badge, icon }) {
  const location = useLocation()
  const isActive = isPathMatch(location.pathname, to)

  return (
    <SubMenuLink
      dense
      component={NavLink}
      to={to}
      end // React Router v6: prevents matching child routes
      selected={isActive}
      className={isActive ? 'active' : ''}
    >
      {icon ? <ListItemIcon>{icon}</ListItemIcon> : ''}
      <ListItemText inset>{name}</ListItemText>
      {badge ? <LinkBadge label={badge} /> : ''}
    </SubMenuLink>
  )
}

function Sidebar({ classes, staticContext, location, ...rest }) {
  const currentLocation = useLocation()

  const initOpenRoutes = useCallback(() => {
    /* Open collapse element that matches current url */
    const pathName = currentLocation.pathname

    let _routes = {}

    routes.forEach((route, index) => {
      // Check if any child route matches the current path
      const hasActiveChild =
        route.children &&
        route.children.some((child) => isPathMatch(pathName, child.path))

      const isOpen = route.open
      const isHome = route.containsHome && (pathName === '/' || pathName === '')

      _routes = { ..._routes, [index]: hasActiveChild || isOpen || isHome }
    })

    return _routes
  }, [currentLocation.pathname])

  const [openRoutes, setOpenRoutes] = useState(() => initOpenRoutes())

  // Update open routes when location changes to keep active section expanded
  useEffect(() => {
    const updatedRoutes = initOpenRoutes()
    setOpenRoutes(updatedRoutes)
  }, [initOpenRoutes])

  const toggle = (index) => {
    // Collapse all elements
    Object.keys(openRoutes).forEach(
      (item) =>
        openRoutes[index] ||
        setOpenRoutes((openRoutes) => ({ ...openRoutes, [item]: false }))
    )

    // Toggle selected element
    setOpenRoutes((openRoutes) => ({
      ...openRoutes,
      [index]: !openRoutes[index]
    }))
  }

  const usertype = localStorage.getItem('usertype')

  if (usertype === 'Owner') {
    routes = sidebarRoutes_Owner
  } else if (usertype === 'Admin') {
    routes = sidebarRoutes_Admin
  } else if (usertype === 'HAdmin') {
    routes = sidebarRoutes_HAdmin
  } else if (usertype === 'FOwner') {
    routes = sidebarRoutes_FOwner
  } else {
    routes = []
  }

  return (
    <Drawer variant="permanent" {...rest}>
      <Brand component={NavLink} to="/dashboard/default" button>
        <Box>
          <img
            alt="NGX"
            src="/static/img/avatars/logo.png"
            width="110"
            height="33"
          />
        </Box>
      </Brand>
      <Scrollbar>
        <List
          disablePadding
          sx={{
            background: (theme) => theme.sidebar.background,
            minHeight: '100vh',
            margin: 0,
            padding: 0
          }}
        >
          <Items>
            {routes.map((category, index) => (
              <React.Fragment key={index}>
                {category.header ? (
                  <SidebarSection>{category.header}</SidebarSection>
                ) : null}

                {category.children && category.icon ? (
                  <React.Fragment key={index}>
                    <SidebarCategory
                      isOpen={!openRoutes[index]}
                      isCollapsable
                      name={category.id}
                      icon={category.icon}
                      children={category.children}
                      onClick={() => toggle(index)}
                    />

                    <Collapse
                      in={openRoutes[index]}
                      timeout="auto"
                      unmountOnExit
                      sx={{ background: 'transparent' }}
                    >
                      <SubMenuWrapper>
                        {category.children.map((route, idx) => (
                          <SidebarLink
                            key={route.path || idx}
                            name={route.name}
                            to={route.path}
                            icon={route.icon}
                            badge={route.badge}
                          />
                        ))}
                      </SubMenuWrapper>
                    </Collapse>
                  </React.Fragment>
                ) : category.icon ? (
                  <NavLink
                    to={category.path}
                    style={{ textDecoration: 'none' }}
                  >
                    {({ isActive }) => (
                      <SidebarCategory
                        isCollapsable={false}
                        name={category.id}
                        to={category.path}
                        icon={category.icon}
                        badge={category.badge}
                        selected={isActive}
                        className={isActive ? 'active' : ''}
                      />
                    )}
                  </NavLink>
                ) : null}
              </React.Fragment>
            ))}
          </Items>
        </List>
      </Scrollbar>
      {/* <SidebarFooter>
        <Grid container spacing={2}>
          <Grid item>
            <SidebarFooterBadge
              overlap="circular"
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              variant="dot"
            >
              <Avatar alt="Lucy Lavender" src="/static/img/avatars/avatar-1.jpg" />
            </SidebarFooterBadge>
          </Grid>
          <Grid item>
            <SidebarFooterText variant="body2">Lucy Lavender</SidebarFooterText>
            <SidebarFooterSubText variant="caption">UX Designer</SidebarFooterSubText>
          </Grid>
        </Grid>
      </SidebarFooter> */}
    </Drawer>
  )
}

// export default withRouter(Sidebar)
export default Sidebar
